
import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Loader2, UploadCloud, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface FileUploadInputProps {
  mediaType: 'image' | 'video';
  onFileUploaded: (url: string, type: 'image' | 'video', file: File) => void;
  disabled?: boolean;
}

export const FileUploadInput = ({ mediaType, onFileUploaded, disabled = false }: FileUploadInputProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadStage, setUploadStage] = useState<'preparing' | 'uploading' | 'finalizing' | 'complete'>('preparing');
  const { toast } = useToast();
  const { user } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Configure accepted file types
  const acceptedFileTypes = mediaType === 'image' 
    ? '.jpg,.jpeg,.png,.gif,.webp'
    : '.mp4,.webm,.mov,.avi';

  const maxFileSizeMB = mediaType === 'image' ? 5 : 100; // 5MB for images, 100MB for videos
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    // Validate file type
    const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
    const validExtensions = acceptedFileTypes.split(',').map(ext => ext.replace('.', ''));
    
    if (!validExtensions.includes(fileExtension)) {
      setError(`Invalid file type. Please upload ${acceptedFileTypes} files.`);
      setFile(null);
      return;
    }
    
    // Validate file size
    if (selectedFile.size > maxFileSizeMB * 1024 * 1024) {
      setError(`File size exceeds ${maxFileSizeMB}MB limit.`);
      setFile(null);
      return;
    }
    
    setError(null);
    setFile(selectedFile);
  };

  // Standard file upload for images
  const handleImageUpload = async (file: File) => {
    if (!user) return;
    
    setUploading(true);
    setProgress(0);
    setError(null);
    
    try {
      // Create a unique file path with user ID and timestamp to avoid collisions
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${user.id}_${timestamp}.${fileExtension}`;
      const filePath = `${mediaType}s/${fileName}`;
      
      // Create upload options
      const { data, error: uploadError } = await supabase.storage
        .from('linkedin-media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) {
        throw uploadError;
      }
      
      // Since we can't track progress directly with Supabase's upload function,
      // set progress to 100% after successful upload
      setProgress(100);
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('linkedin-media')
        .getPublicUrl(filePath);
      
      // Call the callback with the uploaded file URL
      onFileUploaded(publicUrl, mediaType, file);
      
      toast({
        title: 'Upload success',
        description: 'Your file has been uploaded successfully.',
      });
      
      // Reset the file input
      setFile(null);
    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message || 'An error occurred during upload.');
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload file. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  // LinkedIn Video API upload for videos
  const handleVideoUpload = async (file: File) => {
    if (!user) return;
    
    setUploading(true);
    setProgress(0);
    setError(null);
    setUploadStage('preparing');
    
    try {
      // Create a unique file path with user ID and timestamp to avoid collisions
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop();
      const fileName = `${user.id}_${timestamp}.${fileExtension}`;
      const filePath = `${mediaType}s/${fileName}`;
      
      // Initialize upload with LinkedIn API
      setUploadStage('preparing');
      const initResponse = await supabase.functions.invoke('linkedin-video-upload', {
        body: {
          action: 'initializeUpload',
          userId: user.id,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          filePath
        }
      });

      if (initResponse.error) {
        throw new Error(initResponse.error);
      }

      if (!initResponse.data || !initResponse.data.uploadId) {
        throw new Error('Failed to initialize upload with LinkedIn API');
      }

      const { uploadId, uploadUrl, assetId, recordId } = initResponse.data;
      
      // Prepare file for chunked upload
      const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
      const chunks = Math.ceil(file.size / CHUNK_SIZE);
      const parts: { partNumber: number; eTag: string }[] = [];
      
      // Create a new abort controller for this upload
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      
      // Upload chunks
      setUploadStage('uploading');
      for (let i = 0; i < chunks; i++) {
        // Check if upload was aborted
        if (signal.aborted) {
          throw new Error('Upload cancelled');
        }
        
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);
        
        // Convert chunk to array buffer
        const arrayBuffer = await chunk.arrayBuffer();
        const buffer = Array.from(new Uint8Array(arrayBuffer));
        
        // Upload chunk
        const partResponse = await supabase.functions.invoke('linkedin-video-upload', {
          body: {
            action: 'uploadPart',
            userId: user.id,
            uploadId,
            uploadUrl,
            partNumber: i + 1,
            buffer
          }
        });

        if (partResponse.error) {
          throw new Error(`Failed to upload part ${i + 1}: ${partResponse.error}`);
        }

        parts.push({
          partNumber: partResponse.data.partNumber,
          eTag: partResponse.data.eTag
        });
        
        // Update progress
        setProgress(Math.round(((i + 1) / chunks) * 90)); // Use 90% for chunks, 10% for finalization
      }
      
      // Finalize upload
      setUploadStage('finalizing');
      const finalizeResponse = await supabase.functions.invoke('linkedin-video-upload', {
        body: {
          action: 'finalizeUpload',
          userId: user.id,
          uploadId,
          assetId,
          parts
        }
      });

      if (finalizeResponse.error) {
        throw new Error(`Failed to finalize upload: ${finalizeResponse.error}`);
      }
      
      // Complete upload
      setProgress(100);
      setUploadStage('complete');
      
      // Call the callback with the LinkedIn asset URL
      onFileUploaded(finalizeResponse.data.mediaUrl, 'video', file);
      
      toast({
        title: 'Upload success',
        description: 'Your video has been uploaded to LinkedIn successfully.',
      });
      
      // Reset the file input
      setFile(null);
    } catch (error: any) {
      console.error('LinkedIn video upload error:', error);
      setError(error.message || 'An error occurred during upload.');
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload video to LinkedIn. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      abortControllerRef.current = null;
    }
  };
  
  // Handle file upload
  const handleUpload = async () => {
    if (!file || !user) return;
    
    if (mediaType === 'image') {
      await handleImageUpload(file);
    } else {
      await handleVideoUpload(file);
    }
  };
  
  // Cancel ongoing upload
  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      
      setUploading(false);
      setProgress(0);
      toast({
        title: 'Upload cancelled',
        description: 'The file upload has been cancelled.',
      });
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Input
            type="file"
            accept={acceptedFileTypes}
            onChange={handleFileChange}
            disabled={uploading || disabled}
            className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
          />
          {file && !uploading && (
            <Button 
              type="button" 
              onClick={handleUpload}
              disabled={uploading || disabled}
              className="whitespace-nowrap"
            >
              <UploadCloud className="mr-2 h-4 w-4" />
              Upload
            </Button>
          )}
        </div>
        
        {file && !uploading && !error && (
          <p className="text-sm text-muted-foreground">
            Selected file: {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
          </p>
        )}
        
        {error && (
          <div className="flex items-center text-destructive gap-2 text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
      
      {uploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm font-medium">
                {mediaType === 'video' 
                  ? `${uploadStage === 'preparing' ? 'Preparing'
                    : uploadStage === 'uploading' ? 'Uploading parts'
                    : uploadStage === 'finalizing' ? 'Finalizing'
                    : 'Completing'} video upload...`
                  : 'Uploading...'}
              </span>
              <span className="text-sm text-muted-foreground">{progress}%</span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleCancelUpload}
              className="h-8"
              disabled={mediaType === 'image' || uploadStage === 'finalizing' || uploadStage === 'complete'}
            >
              Cancel
            </Button>
          </div>
          <Progress value={progress} className="h-2" />
          {mediaType === 'video' && (
            <p className="text-xs text-muted-foreground">
              {uploadStage === 'preparing' ? 'Preparing video for upload to LinkedIn...'
               : uploadStage === 'uploading' ? 'Uploading video in parts to LinkedIn...'
               : uploadStage === 'finalizing' ? 'Finalizing video upload with LinkedIn...'
               : 'Upload complete!'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
